import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const blogPosts = [
  {
    slug: '/blog/what-is-a-lady-bird-deed-florida',
    title: 'What Is a Lady Bird Deed in Florida? The Complete Guide',
    description: 'Everything you need to know about Lady Bird Deeds in Florida — how they work, who can use them, and why they\'re so powerful.',
    date: 'January 20, 2025',
    readTime: '10 min read'
  },
  {
    slug: '/blog/lady-bird-deed-vs-living-trust-florida',
    title: 'Lady Bird Deed vs Living Trust in Florida — Which Do You Need?',
    description: 'An honest comparison of Lady Bird Deeds and living trusts. When each makes sense, cost differences, and how to choose.',
    date: 'January 20, 2025',
    readTime: '9 min read'
  },
  {
    slug: '/blog/how-much-does-a-lady-bird-deed-cost-florida',
    title: 'How Much Does a Lady Bird Deed Cost in Florida in 2026?',
    description: 'Transparent pricing for Lady Bird Deeds in Florida. What\'s included, what affects cost, and why the attorney matters.',
    date: 'January 20, 2025',
    readTime: '7 min read'
  },
  {
    slug: '/blog/lady-bird-deed-requirements-florida',
    title: 'Lady Bird Deed Requirements in Florida — What You Need to Know',
    description: 'Who qualifies for a Lady Bird Deed, what documents you need, and the step-by-step process from start to finish.',
    date: 'January 20, 2025',
    readTime: '8 min read'
  },
  {
    slug: '/blog/does-lady-bird-deed-avoid-probate-florida',
    title: 'Does a Lady Bird Deed Avoid Probate in Florida? Yes — Here Is How',
    description: 'How Lady Bird Deeds avoid probate, what your heirs need to do, and how it compares to the probate process.',
    date: 'January 20, 2025',
    readTime: '7 min read'
  }
];

const BlogIndex = () => {
  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Blog — Florida Estate Planning Articles | Spanski Law</title>
        <meta name="description" content="Expert articles about Lady Bird Deeds in Florida. Learn about costs, requirements, probate avoidance, and more from attorney Season Spanski." />
        <link rel="canonical" href="https://thelawyerlady.com/blog" />
        <meta property="og:title" content="Lady Bird Deed Blog — Florida Estate Planning Articles" />
        <meta property="og:description" content="Expert articles about Lady Bird Deeds in Florida from attorney Season Spanski." />
        <meta property="og:url" content="https://thelawyerlady.com/blog" />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thelawyerlady.com" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://thelawyerlady.com/blog" }
          ]
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <Link to="/" className="hover:text-[#c9a87c] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[#c9a87c]">Blog</span>
          </nav>
        </div>

        {/* Header */}
        <header className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl md:text-5xl text-[#faf9f7] mb-6">
            Lady Bird Deed Articles
          </h1>
          <p className="font-sans text-xl text-[#faf9f7]/70 max-w-2xl">
            Straightforward explanations of Lady Bird Deeds, Florida estate planning, and how to protect your home. Written by Season Spanski, Florida attorney.
          </p>
        </header>

        {/* Blog Posts */}
        <section className="max-w-4xl mx-auto px-6 py-8">
          <div className="space-y-8">
            {blogPosts.map((post, index) => (
              <article 
                key={index}
                className="group bg-gradient-to-br from-[#141414] to-[#0f0f0f] border border-[#c9a87c]/10 rounded-lg p-6 md:p-8 hover:border-[#c9a87c]/30 transition-colors"
              >
                <div className="flex items-center gap-4 text-sm text-[#faf9f7]/50 mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{post.date}</span>
                  </div>
                  <span>•</span>
                  <span>{post.readTime}</span>
                </div>
                
                <h2 className="font-serif text-2xl text-[#faf9f7] mb-3 group-hover:text-[#c9a87c] transition-colors">
                  <Link to={post.slug}>{post.title}</Link>
                </h2>
                
                <p className="font-sans text-[#faf9f7]/70 mb-4 leading-relaxed">
                  {post.description}
                </p>
                
                <Link 
                  to={post.slug}
                  className="inline-flex items-center gap-2 text-[#c9a87c] hover:text-[#d4b896] font-sans text-sm transition-colors"
                >
                  Read Article
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="text-center">
            <h2 className="font-serif text-2xl text-[#faf9f7] mb-4">Have Questions About Lady Bird Deeds?</h2>
            <p className="text-[#faf9f7]/70 mb-6">
              Call 941-206-2223 or <Link to="/#contact" className="text-[#c9a87c] hover:underline">schedule a free consultation</Link>.
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogIndex;
